import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { firestore } from "../firebase";

// --- MUI IMPORTS ---
import {
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

const statusOptions = [
  "New",
  "In-progress",
  "Cancelled",
  "On-Hold",
  "Completed",
];

const generateDueDatesTable = (
  startDateStr,
  durationInMonths,
  frequencyPerContract
) => {
  const dueDates = [];
  const interval = Math.floor(durationInMonths / frequencyPerContract);
  const startDate = new Date(startDateStr);

  for (let i = 0; i < frequencyPerContract; i++) {
    const dueDate = new Date(startDate);
    dueDate.setMonth(startDate.getMonth() + interval * i);
    const iso = dueDate.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm
    dueDates.push({
      id: i,
      DueDate: iso,
      Status: "New",
      AssignedTo: "",
      AssignedChecklist: "",
    });
  }
  return dueDates;
};

const AMCScheduleForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    AMCID: "",
    AMCName: "",
    CustomerName: "",
    CustomerAddress: "",
    CustomerEmailID: "",
    CustomerMobileNo: "",
    ContractID: "",
    ContractName: "",
    AMCScheduleStartDate: "",
    UnitForDurationOfContract: "months",
    ValueForDurationOfContraNumber: "",
    FreqOfMaintenance: "",
    Reference: "",
    IsActive: true,
  });

  const [previewDueDatesTable, setPreviewDueDatesTable] = useState([]);
  const [currentUserEmail, setCurrentUserEmail] = useState("");
  const [userRole, setUserRole] = useState("");
  const [technicianOptions, setTechnicianOptions] = useState([]);
  const [availableChecklists, setAvailableChecklists] = useState([]);
  const [frozenContracts, setFrozenContracts] = useState([]);

  const normalizedUserRole = String(userRole || "").trim().toLowerCase();
  const isCustomerView = !["admin", "superadmin", "facility_admin"].includes(
    normalizedUserRole
  );
  const canSkipContract = ["facility_admin", "customer"].includes(
    normalizedUserRole
  );

  // --- UPDATED DATA LOADING LOGIC WITH DEBUGGING ---
  useEffect(() => {
    const loadData = async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      const tenantId = sessionStorage.getItem("tenantId");
      const userRole = sessionStorage.getItem("userRole");

      // DEBUG: Print these to your console to verify them
      console.log("--- DEBUG LOAD ---");
      console.log("User:", user?.email);
      console.log("TenantID:", tenantId);
      console.log("Role:", userRole);

      if (user) {
        setCurrentUserEmail(user.email);
      }
      setUserRole(sessionStorage.getItem("userRole") || "customer");

      // 1. Fetch Technicians
      try {
        const techQueryConstraints = [];
        if (userRole !== "superadmin" && tenantId) {
          techQueryConstraints.push(where("tenantId", "==", tenantId));
        }

        if (userRole === "superadmin" || tenantId) {
          const q = query(
            collection(firestore, "UserProfileMaster"),
            ...techQueryConstraints
          );

          const snapshot = await getDocs(q);
          console.log(`UserProfileMaster Found: ${snapshot.docs.length}`);

          const techs = snapshot.docs
            .map((doc) => ({ id: doc.id, ...doc.data() }))
            .filter((d) =>
              d.role === "technician" || d.role === "Technician"
            )
            .map((d) => ({
              value: d.email || d.id,
              label:
                d.displayName || d.name || d.firstName || d.email ||
                "Unknown Technician",
            }));

          console.log(`Technicians Found: ${techs.length}`);
          setTechnicianOptions(techs);
        } else {
          console.warn("Skipping Technician Fetch: Missing TenantID");
          setTechnicianOptions([]);
        }
      } catch (error) {
        console.error("Error fetching technicians:", error);
      }

      // 2. Fetch Checklists
      try {
        const checklistDocsById = new Map();
        const addChecklistDocs = (snapshot) => {
          snapshot.docs.forEach((entry) => {
            checklistDocsById.set(entry.id, {
              id: entry.id,
              ...entry.data(),
            });
          });
        };

        if (userRole === "superadmin") {
          addChecklistDocs(await getDocs(collection(firestore, "checklists")));
        } else {
          if (tenantId) {
            addChecklistDocs(
              await getDocs(
                query(
                  collection(firestore, "checklists"),
                  where("tenantId", "==", tenantId)
                )
              )
            );
          }

          if (user?.email) {
            addChecklistDocs(
              await getDocs(
                query(
                  collection(firestore, "checklists"),
                  where("createdBy", "==", user.email)
                )
              )
            );
            addChecklistDocs(
              await getDocs(
                query(
                  collection(firestore, "checklists"),
                  where("assignedTo", "==", user.email)
                )
              )
            );
          }
        }

        const checklists = Array.from(checklistDocsById.values())
          .filter((checklist) => checklist.IsActive !== false)
          .map((checklist) => ({
            id: checklist.id,
            name: checklist.name || "Unnamed Checklist",
            description: checklist.description,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        console.log(`Checklists Found: ${checklists.length}`);
        setAvailableChecklists(checklists);
      } catch (error) {
        console.error("Error fetching checklists:", error);
      }

      // 3. Fetch Frozen Contracts
      try {
        const contractQueryConstraints = [where("isFrozen", "==", true)];
        if (userRole !== "superadmin" && tenantId) {
          contractQueryConstraints.push(where("tenantId", "==", tenantId));
        }

        if (userRole === "superadmin" || tenantId) {
          const contractsQuery = query(
            collection(firestore, "ContractMaster"),
            ...contractQueryConstraints
          );

          const contractsSnapshot = await getDocs(contractsQuery);
          console.log(`Frozen Contracts Found: ${contractsSnapshot.docs.length}`);

          const contracts = contractsSnapshot.docs.map((doc) => ({
            id: doc.id,
            contractId: doc.data().contractId || doc.id,
            contractTitle: doc.data().contractTitle || "Unnamed Contract",
            ...doc.data(),
          }));
          setFrozenContracts(contracts);
        }
      } catch (error) {
        console.error("Error fetching frozen contracts:", error);
      }

      // 4. Load existing schedule data
      if (isEditMode) {
        try {
          const docRef = doc(firestore, "AMCScheduleMaster", id);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            setFormData(data);
            setPreviewDueDatesTable(data.DueDatesTable || []);
          } else {
            alert("Schedule not found");
            navigate("/amc/schedule/list");
          }
        } catch (err) {
          console.error("Error loading schedule:", err);
          alert("Error: You may not have permission to view this schedule.");
          navigate("/amc/schedule/list");
        }
      }
    };

    loadData();
  }, [id, isEditMode, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updatedForm = {
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    };
    setFormData(updatedForm);

    if (
      [
        "AMCScheduleStartDate",
        "ValueForDurationOfContraNumber",
        "FreqOfMaintenance",
      ].includes(name)
    ) {
      const duration = parseInt(updatedForm.ValueForDurationOfContraNumber);
      const frequency = parseInt(updatedForm.FreqOfMaintenance);
      const startDate = updatedForm.AMCScheduleStartDate;

      if (
        startDate &&
        duration &&
        frequency &&
        updatedForm.UnitForDurationOfContract === "months"
      ) {
        const preview = generateDueDatesTable(startDate, duration, frequency);
        setPreviewDueDatesTable(preview);
      } else {
        setPreviewDueDatesTable([]);
      }
    }
  };

  const handleDueDateChange = (index, field, value) => {
    const updatedTable = [...previewDueDatesTable];
    const [datePart, timePart] = (updatedTable[index].DueDate || "T").split(
      "T"
    );

    if (field === "DueDate") {
      updatedTable[index].DueDate = `${value}T${timePart || "09:00"}`;
    } else if (field === "DueTime") {
      updatedTable[index].DueDate = `${datePart}T${value}`;
    } else {
      updatedTable[index][field] = value;
    }

    setPreviewDueDatesTable(updatedTable);
  };

  const handleChecklistChange = (index, checklistId) => {
    const updatedTable = [...previewDueDatesTable];
    updatedTable[index].AssignedChecklist = checklistId;
    setPreviewDueDatesTable(updatedTable);
  };

  const handleContractSelect = (selectedContractId) => {
    const selectedContract = frozenContracts.find(
      (contract) => contract.id === selectedContractId
    );
    if (selectedContract) {
      setFormData((prev) => ({
        ...prev,
        ContractID: selectedContract.contractId || "",
        ContractName: selectedContract.contractTitle || "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const tenantId = sessionStorage.getItem("tenantId");
    const userRole = sessionStorage.getItem("userRole");

    if (userRole === "superadmin") {
      alert("Error: Super Admins cannot create or edit tenant schedules.");
      return;
    }
    if (!tenantId) {
      alert("Error: No Tenant ID found. Cannot save schedule.");
      return;
    }

    const selectedFrozenContract = frozenContracts.find(
      (contract) => contract.contractId === formData.ContractID
    );

    if (!canSkipContract) {
      if (!formData.ContractID.trim()) {
        alert("Please select a frozen contract before submitting.");
        return;
      } else if (frozenContracts.length > 0 && !selectedFrozenContract) {
        alert("Invalid Contract ID. Please select a valid frozen contract.");
        return;
      }

      if (!formData.ContractName.trim()) {
        alert("Contract Name is missing. Please select a frozen contract.");
        return;
      }
    } else if (formData.ContractID.trim() && frozenContracts.length > 0 && !selectedFrozenContract) {
      alert("Invalid Contract ID. Please select a valid frozen contract.");
      return;
    }

    try {
      const dueDatesTable = previewDueDatesTable;
      const now = Timestamp.now();
      const normalizedCurrentUserEmail = String(currentUserEmail || "").trim().toLowerCase();
      const payload = {
        ...formData,
        updatedBy: normalizedCurrentUserEmail,
        updatedAt: now,
        DueDatesTable: dueDatesTable,
        tenantId: tenantId,
      };

      if (isEditMode) {
        const docRef = doc(firestore, "AMCScheduleMaster", id);
        await setDoc(docRef, payload, { merge: true });
        alert("AMC Schedule Updated Successfully!");
      } else {
        const collectionRef = collection(firestore, "AMCScheduleMaster");
        const newDocRef = doc(collectionRef);
        const amcIdFromDoc = newDocRef.id;

        await setDoc(newDocRef, {
          ...payload,
          AMCID: amcIdFromDoc,
          createdBy: normalizedCurrentUserEmail,
          createdAt: now,
          TasksList: [],
          ScheduleList: [],
        });
        alert("AMC Scheduled Successfully!");
      }
      navigate("/amc/schedule/list");
    } catch (error) {
      console.error("Error saving document:", error);
      alert("Failed to save schedule");
    }
  };

  const handleCancel = () => {
    const message = isEditMode
      ? "Discard changes to this schedule and go back?"
      : "Discard this new schedule and go back?";
    if (window.confirm(message)) {
      navigate("/amc/schedule/list");
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3, bgcolor: "grey.100" }}>
      <Container maxWidth="md">
        <Paper sx={{ p: { xs: 2, md: 4 }, boxShadow: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {isEditMode ? "Edit Maintenance Schedule" : "Create Maintenance Schedule"}
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ mt: 3 }}
          >
            <Grid container spacing={3}>
              {isEditMode && (
                <Grid item xs={12}>
                  <TextField
                    label="AMC ID"
                    name="AMCID"
                    value={formData.AMCID}
                    fullWidth
                    disabled
                    variant="outlined"
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="AMC Name"
                  name="AMCName"
                  value={formData.AMCName}
                  onChange={handleChange}
                  required
                  fullWidth
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Customer Name"
                  name="CustomerName"
                  value={formData.CustomerName}
                  onChange={handleChange}
                  required
                  fullWidth
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Customer Address"
                  name="CustomerAddress"
                  value={formData.CustomerAddress}
                  onChange={handleChange}
                  fullWidth
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Customer Email ID"
                  name="CustomerEmailID"
                  type="email"
                  value={formData.CustomerEmailID}
                  onChange={handleChange}
                  required
                  fullWidth
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Customer Mobile No"
                  name="CustomerMobileNo"
                  type="tel"
                  value={formData.CustomerMobileNo}
                  onChange={handleChange}
                  required
                  fullWidth
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required={!canSkipContract}>
                  <InputLabel id="contract-select-label">
                    Select Contract{canSkipContract ? " (optional)" : ""}
                  </InputLabel>
                  <Select
                    labelId="contract-select-label"
                    label="Select Contract"
                    value={
                      frozenContracts.find(
                        (contract) =>
                          contract.contractId === formData.ContractID
                      )?.id || ""
                    }
                    onChange={(e) => handleContractSelect(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="" />
                    {frozenContracts.length > 0 ? (
                      frozenContracts.map((contract) => (
                        <MenuItem key={contract.id} value={contract.id}>
                          {contract.contractTitle} (ID: {contract.contractId})
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled value="no-contract">
                        <em>No Frozen Contracts Found</em>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Contract ID"
                  name="ContractID"
                  value={formData.ContractID}
                  fullWidth
                  disabled
                  variant="outlined"
                  helperText="Auto-filled from selected contract"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Contract Name"
                  name="ContractName"
                  value={formData.ContractName}
                  fullWidth
                  disabled
                  variant="outlined"
                  helperText="Auto-filled from selected contract"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Schedule Start Date"
                  name="AMCScheduleStartDate"
                  type="date"
                  value={formData.AMCScheduleStartDate}
                  onChange={handleChange}
                  required
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel id="duration-unit-label">
                    Duration Unit
                  </InputLabel>
                  <Select
                    labelId="duration-unit-label"
                    label="Duration Unit"
                    name="UnitForDurationOfContract"
                    value={formData.UnitForDurationOfContract}
                    onChange={handleChange}
                  >
                    {["days", "weeks", "months", "years"].map((unit) => (
                      <MenuItem key={unit} value={unit}>
                        {unit}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel id="duration-value-label">
                    Duration Value
                  </InputLabel>
                  <Select
                    labelId="duration-value-label"
                    label="Duration Value"
                    name="ValueForDurationOfContraNumber"
                    value={formData.ValueForDurationOfContraNumber}
                    onChange={handleChange}
                  >
                    {Array.from({ length: 20 }, (_, i) =>
                      (i + 1).toString()
                    ).map((val) => (
                      <MenuItem key={val} value={val}>
                        {val}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel id="frequency-label">
                    Frequency of Maintenance
                  </InputLabel>
                  <Select
                    labelId="frequency-label"
                    label="Frequency of Maintenance"
                    name="FreqOfMaintenance"
                    value={formData.FreqOfMaintenance}
                    onChange={handleChange}
                  >
                    {Array.from({ length: 20 }, (_, i) =>
                      (i + 1).toString()
                    ).map((val) => (
                      <MenuItem key={val} value={val}>
                        {val}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Reference"
                  name="Reference"
                  value={formData.Reference}
                  onChange={handleChange}
                  fullWidth
                  variant="outlined"
                />
              </Grid>

              {/* Due Dates Preview Table */}
              {previewDueDatesTable.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Generated Due Dates Preview
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Due Date / Time
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Status
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Technician
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Checklist
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewDueDatesTable.map((item, idx) => {
                          const [datePart, timePart] = (
                            item.DueDate || "T"
                          ).split("T");
                          return (
                            <TableRow key={item.id}>
                              <TableCell sx={{ padding: "6px 8px" }}>
                                <Box sx={{ display: "flex", gap: 1 }}>
                                  <TextField
                                    type="date"
                                    size="small"
                                    value={datePart}
                                    onChange={(e) =>
                                      handleDueDateChange(
                                        idx,
                                        "DueDate",
                                        e.target.value
                                      )
                                    }
                                    sx={{ flex: 1 }}
                                  />
                                  <TextField
                                    type="time"
                                    size="small"
                                    value={timePart || "09:00"}
                                    onChange={(e) =>
                                      handleDueDateChange(
                                        idx,
                                        "DueTime",
                                        e.target.value
                                      )
                                    }
                                    sx={{ width: "90px" }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell sx={{ padding: "6px 8px" }}>
                                {isCustomerView ? (
                                  <TextField
                                    value={
                                      item.Status && item.Status !== "New"
                                        ? item.Status
                                        : "Will be updated"
                                    }
                                    size="small"
                                    disabled
                                    fullWidth
                                  />
                                ) : (
                                  <Select
                                    value={item.Status}
                                    onChange={(e) =>
                                      handleDueDateChange(
                                        idx,
                                        "Status",
                                        e.target.value
                                      )
                                    }
                                    size="small"
                                    fullWidth
                                  >
                                    {statusOptions.map((s) => (
                                      <MenuItem key={s} value={s}>
                                        {s}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                )}
                              </TableCell>

                              {/* TECHNICIAN DROPDOWN */}
                              <TableCell sx={{ padding: "6px 8px" }}>
                                {isCustomerView ? (
                                  <TextField
                                    value={
                                      technicianOptions.find(
                                        (tech) => tech.value === item.AssignedTo
                                      )?.label || "Will be updated"
                                    }
                                    size="small"
                                    disabled
                                    fullWidth
                                  />
                                ) : (
                                  <Select
                                    value={item.AssignedTo}
                                    onChange={(e) =>
                                      handleDueDateChange(
                                        idx,
                                        "AssignedTo",
                                        e.target.value
                                      )
                                    }
                                    displayEmpty
                                    size="small"
                                    fullWidth
                                  >
                                    <MenuItem value="">
                                      <em>-- Select --</em>
                                    </MenuItem>
                                    {technicianOptions.length > 0 ? (
                                      technicianOptions.map((tech) => (
                                        <MenuItem key={tech.value} value={tech.value}>
                                          {tech.label}
                                        </MenuItem>
                                      ))
                                    ) : (
                                      <MenuItem disabled value="no-tech">
                                        <em>No Technicians Found</em>
                                      </MenuItem>
                                    )}
                                  </Select>
                                )}
                              </TableCell>

                              {/* CHECKLIST DROPDOWN */}
                              <TableCell sx={{ padding: "6px 8px" }}>
                                {isCustomerView ? (
                                  <TextField
                                    value={
                                      availableChecklists.find(
                                        (checklist) => checklist.id === item.AssignedChecklist
                                      )?.name || "Will be updated"
                                    }
                                    size="small"
                                    disabled
                                    fullWidth
                                  />
                                ) : (
                                  <Select
                                    value={item.AssignedChecklist || ""}
                                    onChange={(e) =>
                                      handleChecklistChange(idx, e.target.value)
                                    }
                                    displayEmpty
                                    size="small"
                                    fullWidth
                                  >
                                    <MenuItem value="">
                                      <em>-- Select --</em>
                                    </MenuItem>
                                    {availableChecklists.length > 0 ? (
                                      availableChecklists.map((checklist) => (
                                        <MenuItem
                                          key={checklist.id}
                                          value={checklist.id}
                                        >
                                          {checklist.name}
                                        </MenuItem>
                                      ))
                                    ) : (
                                      <MenuItem disabled value="no-checklist">
                                        <em>No Checklists Found</em>
                                      </MenuItem>
                                    )}
                                  </Select>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Metadata Box */}
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      border: "1px solid #ddd",
                      borderRadius: 1,
                      bgcolor: "grey.50",
                    }}
                  >
                    <Typography variant="body2">
                      <strong>Created By:</strong>{" "}
                      {formData.createdBy || currentUserEmail || "—"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Created At:</strong>{" "}
                      {formData.createdAt
                        ? new Date(
                            formData.createdAt.seconds * 1000
                          ).toLocaleString()
                        : "—"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Last Updated By:</strong>{" "}
                      {formData.updatedBy || currentUserEmail || "—"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Last Updated At:</strong>{" "}
                      {formData.updatedAt
                        ? new Date(
                            formData.updatedAt.seconds * 1000
                          ).toLocaleString()
                        : "—"}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* Checkbox and Buttons */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="IsActive"
                      checked={formData.IsActive}
                      onChange={handleChange}
                    />
                  }
                  label="Is Active"
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    color="secondary"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" color="primary">
                    {isEditMode ? "Update Schedule" : "Submit"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default AMCScheduleForm;
